/**
 * The Windows app's local store (apps/desktop/src/main/store.ts), held in this
 * tab's memory instead of an encrypted SQLite file. Same methods, same
 * results, for the parts the offline engine uses: every row comes back as a
 * fresh copy, exactly as SQLite hands back freshly parsed JSON.
 */
export type Row = Record<string, unknown>;
type Stored = { data: Row; version: number; provisional: boolean; patientId: string | null };
type Versioned = Row & { __version: number; __provisional: boolean };

export interface DemoSession {
  userId: string;
  email: string;
  verifier: string;
  me: Record<string, unknown>;
  cookies: Record<string, string>;
  signedInAt: string;
  offlineSession: boolean;
}

const copy = (s: Stored): Versioned => ({ ...structuredClone(s.data), __version: s.version, __provisional: s.provisional });

export class MemoryStore {
  private readonly tables = new Map<string, Map<string, Stored>>();
  private readonly responses = new Map<string, unknown>();
  private readonly blobs = new Map<string, { mimeType: string; byteSize: number; sha256: string; localPath: string; origin: string }>();
  private current: DemoSession | null = null;

  private table(entity: string): Map<string, Stored> {
    let t = this.tables.get(entity);
    if (!t) this.tables.set(entity, (t = new Map()));
    return t;
  }

  upsertRow(entity: string, id: string, data: Row, meta: { patientId?: string | null; serverVersion?: number; provisional?: boolean } = {}): void {
    const patientId = meta.patientId ?? (typeof data.patient_id === 'string' ? data.patient_id : entity === 'patient' ? id : null);
    this.table(entity).set(id, { data: structuredClone(data), version: meta.serverVersion ?? Number(data.version ?? 1), provisional: !!meta.provisional, patientId });
  }

  deleteRow(entity: string, id: string): void {
    this.table(entity).delete(id);
  }

  patchRow(entity: string, id: string, patch: Row): void {
    const r = this.table(entity).get(id);
    if (r) r.data = { ...r.data, ...structuredClone(patch) };
  }

  getRow(entity: string, id: string): Versioned | null {
    const r = this.table(entity).get(id);
    return r ? copy(r) : null;
  }

  rows(entity: string, filter: { patientId?: string } = {}): Versioned[] {
    const out: Versioned[] = [];
    for (const r of this.table(entity).values()) if (!filter.patientId || r.patientId === filter.patientId) out.push(copy(r));
    return out;
  }

  count(entity: string): number {
    return this.table(entity).size;
  }

  /** Nothing waits to be sent: the demo has no server to send to. */
  pendingFor(): string[] {
    return [];
  }

  session(): DemoSession | null {
    return this.current ? structuredClone(this.current) : null;
  }

  setSession(s: DemoSession | null): void {
    this.current = s ? structuredClone(s) : null;
  }

  patchSession(patch: Partial<DemoSession>): void {
    if (this.current) this.current = { ...this.current, ...patch };
  }

  cacheResponse(path: string, body: unknown): void {
    this.responses.set(path, body);
  }

  cachedResponse(path: string): { status: number; body: string; capturedAt: string } | null {
    if (!this.responses.has(path)) return null;
    return { status: 200, body: JSON.stringify(this.responses.get(path)), capturedAt: '' };
  }

  putBlob(b: { attachmentId: string; mimeType: string; byteSize: number; sha256: string; localPath: string; origin: string }): void {
    this.blobs.set(b.attachmentId, { ...b });
  }

  blob(attachmentId: string): { mimeType: string; byteSize: number; sha256: string; localPath: string; origin: string } | null {
    const b = this.blobs.get(attachmentId);
    return b ? { ...b } : null;
  }

  transaction<T>(fn: () => T): T {
    return fn();
  }
}
